import { useState, useEffect, useMemo } from 'react'
import { toast } from 'sonner'
import { Dialog } from 'radix-ui'
import { Combobox } from '@/components/common/Combobox'
import { useConfirm } from '@/components/common/ConfirmDialog'
import {
  applyClientSideTableState,
  DataTable,
  useTableState,
  type ColumnDef,
} from '@/components/data-table'
import { BoolIcon } from '@/components/common/BoolIcon'
import { AddButton } from '@/components/common/AddButton'
import {
  useAccessPolicies,
  useCreateAccessPolicy,
  useDeleteAccessPolicy,
  useUpdateAccessPolicy,
} from '../hooks'
import type { AccessPolicy, AccessPolicyRequest, FormSchema } from '../types'

type Props = {
  objectType: string
  schema: FormSchema
}

export function AccessPolicyPanel({ objectType, schema }: Props) {
  const confirm = useConfirm()
  const policiesQ = useAccessPolicies(objectType)
  const del = useDeleteAccessPolicy(objectType)
  const [editing, setEditing] = useState<AccessPolicy | null>(null)
  const [creating, setCreating] = useState(false)
  const tableId = `access-policies-${objectType}`
  const { state, setSort, setSearch, setFilter, removeFilter, clearFilters } = useTableState(tableId)

  const policies = policiesQ.data ?? []

  const columns: ColumnDef<AccessPolicy>[] = useMemo(
    () => [
      {
        key: 'id',
        header: 'ID',
        sortable: true,
        accessor: (p) => <span className="text-xs">{p.id}</span>,
        className: 'w-16',
      },
      {
        key: 'target',
        header: 'Sekcja / Pole',
        sortable: true,
        filter: { kind: 'text', placeholder: 'Filtruj target...' },
        accessor: (p) => (
          <span className="font-mono text-xs">
            {p.sectionCode ? `sekcja: ${p.sectionCode}` : `pole: ${p.fieldCode}`}
          </span>
        ),
        alwaysVisible: true,
      },
      {
        key: 'roleCode',
        header: 'Rola',
        sortable: true,
        filter: { kind: 'text' },
        accessor: (p) => p.roleCode,
      },
      {
        key: 'workflowState',
        header: 'Stage',
        sortable: true,
        filter: { kind: 'text' },
        accessor: (p) => p.workflowState ?? <span className="text-slate-400">*</span>,
        className: 'whitespace-nowrap',
      },
      {
        key: 'canView',
        header: 'View',
        sortable: true,
        filter: { kind: 'bool' },
        accessor: (p) => <BoolIcon value={p.canView} />,
        className: 'w-20',
      },
      {
        key: 'canEdit',
        header: 'Edit',
        sortable: true,
        filter: { kind: 'bool' },
        accessor: (p) => <BoolIcon value={p.canEdit} />,
        className: 'w-20',
      },
      {
        key: 'effect',
        header: 'Effect',
        sortable: true,
        filter: {
          kind: 'select',
          options: [
            { value: 'GRANT', label: 'GRANT' },
            { value: 'DENY', label: 'DENY' },
          ],
        },
        accessor: (p) => (
          <span
            className={`px-2 py-0.5 rounded text-xs ${
              p.effect === 'GRANT' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {p.effect}
          </span>
        ),
        className: 'whitespace-nowrap',
      },
      {
        key: 'priority',
        header: 'Priority',
        sortable: true,
        accessor: (p) => p.priority,
        className: 'w-20 text-right tabular-nums',
      },
      {
        key: 'actions',
        header: '',
        accessor: (p) => (
          <div className="text-right whitespace-nowrap">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setEditing(p)
              }}
              className="text-xs text-slate-600 px-1"
            >
              Edytuj
            </button>
            <button
              type="button"
              onClick={async (e) => {
                e.stopPropagation()
                const ok = await confirm({
                  title: `Usunąć politykę #${p.id}?`,
                  description: 'Operacja jest nieodwracalna.',
                  confirmLabel: 'Usuń',
                  variant: 'destructive',
                })
                if (ok) del.mutate(p.id, { onError: (err) => toast.error(formatError(err)) })
              }}
              className="text-xs text-red-600 px-1 ml-2"
            >
              Usuń
            </button>
          </div>
        ),
        className: 'w-32',
        alwaysVisible: true,
      },
    ],
    [del, confirm],
  )

  const filteredRows = useMemo(
    () =>
      applyClientSideTableState(policies, columns, state, {
        valueGetters: {
          id: (p) => p.id,
          target: (p) => p.sectionCode ?? p.fieldCode ?? '',
          roleCode: (p) => p.roleCode,
          workflowState: (p) => p.workflowState ?? '',
          canView: (p) => p.canView,
          canEdit: (p) => p.canEdit,
          effect: (p) => p.effect,
          priority: (p) => p.priority,
        },
        searchString: (p) =>
          `${p.sectionCode ?? ''} ${p.fieldCode ?? ''} ${p.roleCode} ${p.effect}`.toLowerCase(),
      }),
    [policies, columns, state],
  )

  return (
    <div className="flex flex-col w-full">
      <header className="px-4 py-3 border-b flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">Uprawnienia (field_access_policy)</h2>
          <p className="text-xs text-slate-500">
            Sekcja × rola × stage → GRANT/DENY (can_view, can_edit). Wyższy priority wygrywa.
          </p>
        </div>
<AddButton onClick={() => setCreating(true)}>Dodaj politykę</AddButton>
      </header>

      <div className="flex-1 overflow-auto px-4 py-3">
        <DataTable<AccessPolicy>
          tableId={tableId}
          data={filteredRows}
          columns={columns}
          rowKey={(p) => p.id}
          isLoading={policiesQ.isLoading}
          isError={!!policiesQ.error}
          errorState={policiesQ.error ? `Błąd: ${String(policiesQ.error)}` : undefined}
          sort={state.sort}
          onSortChange={setSort}
          state={state}
          searchable
          searchPlaceholder="Szukaj sekcji / pola / roli / effect..."
          onSearchChange={setSearch}
          onFilterChange={setFilter}
          onFilterRemove={removeFilter}
          onClearAll={clearFilters}
          emptyState={'Brak polityk. Kliknij "Dodaj politykę".'}
        />
      </div>

      {(creating || editing) && (
        <PolicyFormSheet
          objectType={objectType}
          schema={schema}
          editing={editing}
          onClose={() => { setCreating(false); setEditing(null) }}
        />
      )}
    </div>
  )
}

function PolicyFormSheet({
  objectType, schema, editing, onClose,
}: { objectType: string; schema: FormSchema; editing: AccessPolicy | null; onClose: () => void }) {
  const create = useCreateAccessPolicy(objectType)
  const update = useUpdateAccessPolicy(objectType)

  const [target, setTarget] = useState<'section' | 'field'>(editing?.fieldCode ? 'field' : 'section')
  const [sectionCode, setSectionCode] = useState(editing?.sectionCode ?? '')
  const [fieldCode, setFieldCode] = useState(editing?.fieldCode ?? '')
  const [roleCode, setRoleCode] = useState(editing?.roleCode ?? '')
  const [workflowState, setWorkflowState] = useState(editing?.workflowState ?? '')
  const [canView, setCanView] = useState(editing?.canView ?? true)
  const [canEdit, setCanEdit] = useState(editing?.canEdit ?? false)
  const [effect, setEffect] = useState<'GRANT' | 'DENY'>(editing?.effect ?? 'GRANT')
  const [priority, setPriority] = useState(editing?.priority ?? 100)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => { setError(null) }, [editing])

  const submit = () => {
    setError(null)
    if (target === 'section' && !sectionCode) { setError('Wybierz sekcję'); return }
    if (target === 'field' && !fieldCode) { setError('Wybierz pole'); return }
    if (!roleCode.trim()) { setError('Podaj kod roli'); return }

    const payload: AccessPolicyRequest = {
      sectionCode: target === 'section' ? sectionCode : null,
      fieldCode: target === 'field' ? fieldCode : null,
      roleCode,
      workflowState: workflowState.trim() || null,
      canView, canEdit, effect, priority,
    }

    if (editing) {
      update.mutate(
        { id: editing.id, payload },
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    } else {
      create.mutate(
        payload,
        { onSuccess: onClose, onError: e => setError(formatError(e)) },
      )
    }
  }

  const isSubmitting = create.isPending || update.isPending

  return (
    <Dialog.Root open onOpenChange={o => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed right-0 top-0 z-50 h-full w-[460px] bg-white shadow-xl p-5 overflow-auto">
          <Dialog.Title className="text-base font-semibold">
            {editing ? `Edycja polityki #${editing.id}` : 'Nowa polityka'}
          </Dialog.Title>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">
                <input type="radio" name="target" checked={target === 'section'} onChange={() => setTarget('section')} disabled={!!editing} />
                Sekcja
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="target" checked={target === 'field'} onChange={() => setTarget('field')} disabled={!!editing} />
                Pole
              </label>
            </div>

            {target === 'section' ? (
              <label className="grid gap-1">
                <span className="text-xs uppercase text-slate-500">Sekcja</span>
                <Combobox<string>
                  value={sectionCode || null}
                  onChange={v => setSectionCode(v ?? '')}
                  options={schema.sections.map(s => ({ value: s.code, label: `${s.labelPl} (${s.code})` }))}
                  emptyOptionLabel="— wybierz —"
                  placeholder="Wybierz sekcję..."
                  disabled={isSubmitting || !!editing}
                />
              </label>
            ) : (
              <label className="grid gap-1">
                <span className="text-xs uppercase text-slate-500">Pole</span>
                <Combobox<string>
                  value={fieldCode || null}
                  onChange={v => setFieldCode(v ?? '')}
                  options={schema.fields.map(f => ({ value: f.fieldCode, label: `${f.labelPl} (${f.fieldCode})` }))}
                  emptyOptionLabel="— wybierz —"
                  placeholder="Wybierz pole..."
                  disabled={isSubmitting || !!editing}
                />
              </label>
            )}

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Rola (code)</span>
              <input
                value={roleCode}
                onChange={e => setRoleCode(e.target.value)}
                disabled={isSubmitting}
                className="border rounded px-2 py-1 font-mono"
                placeholder="handlowiec"
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Stage (puste = każdy)</span>
              <input
                value={workflowState}
                onChange={e => setWorkflowState(e.target.value)}
                disabled={isSubmitting}
                className="border rounded px-2 py-1 font-mono"
                placeholder="DRAFT"
              />
            </label>

            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={canView} onChange={e => setCanView(e.target.checked)} disabled={isSubmitting} />
                can_view
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={canEdit} onChange={e => setCanEdit(e.target.checked)} disabled={isSubmitting} />
                can_edit
              </label>
            </div>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Effect</span>
              <Combobox<string>
                value={effect}
                onChange={v => setEffect((v ?? 'GRANT') as 'GRANT' | 'DENY')}
                options={[
                  { value: 'GRANT', label: 'GRANT' },
                  { value: 'DENY', label: 'DENY' },
                ]}
                disabled={isSubmitting}
              />
            </label>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Priority (mniejsza wygrywa)</span>
              <input
                type="number"
                value={priority}
                onChange={e => setPriority(Number(e.target.value))}
                disabled={isSubmitting}
                className="border rounded px-2 py-1"
              />
            </label>

            {error && <p className="text-red-600 text-xs">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button type="button" onClick={onClose} className="px-3 py-1.5 border rounded" disabled={isSubmitting}>
                Anuluj
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={isSubmitting}
                className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50"
              >
                {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd zapisu'
}
